import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { FiDownload, FiTrash2 } from "react-icons/fi";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { saveAs } from "file-saver";

import GenericHeader from "../../GenericHeader";
import SortableTable from "../../Utils/SortableTable";
import Filters from "../../Utils/Filters";
import MultiselectCheck from "../../Utils/MultiselectCheck";
import { formatarData } from "../../../Funcoes/FormatarData";
import { AuthContext } from "../../../Auth/AuthProvider";
import { VerifyPermission } from "../../../Funcoes/VerifyPermission";
import ModalDeleteRac from "../ModalDeleteRac";
import useApi from "../../../hooks/useApi";

const PAGE_SIZE = 100;
const SCROLL_THRESHOLD = 40;
const PERMISSAO_BASE = { modulo: "ssma", submodulo: "rac" };

const spin = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`;

const PageContent = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
    box-sizing: border-box;
    overflow: hidden;
`;

const Statistics = styled.div`
    width: 280px;
    flex-shrink: 0;
    background-color: #f3f3f3;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 12px;
    padding: 10px;
    box-sizing: border-box;

    @media (max-width: 768px) {
        width: 100%;
    }
`;

const ContainerStatistic = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    justify-content: center;
    min-height: 100%;
`;

const TituloStatistic = styled.h3`
    text-align: center;
    margin: 0;
    padding: 0;
    font-size: 13px;
    font-weight: bold;
    color: #000;
`;

const TextoStatistic = styled.p`
    margin: 0;
    padding: 0;
    font-size: 13px;
    color: #333;
`;

const AcoesCelula = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
`;

const BotaoAcao = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease;

    color: ${(p) => (p.$danger ? "#b91c1c" : "#1d4ed8")};
    background-color: ${(p) =>
        p.$danger ? "rgba(185, 28, 28, 0.12)" : "rgba(29, 78, 216, 0.10)"};
    border: 1px solid transparent;

    &:hover:not(:disabled) {
        background-color: ${(p) =>
            p.$danger ? "rgba(185, 28, 28, 0.22)" : "rgba(29, 78, 216, 0.20)"};
        border-color: ${(p) =>
            p.$danger ? "rgba(185, 28, 28, 0.5)" : "rgba(29, 78, 216, 0.45)"};
    }

    &:focus-visible {
        outline: 2px solid ${(p) => (p.$danger ? "#b91c1c" : "#1d4ed8")};
        outline-offset: 2px;
    }

    &:disabled {
        opacity: 0.7;
        cursor: not-allowed;
    }
`;

const IconLoad = styled(AiOutlineLoading3Quarters)`
    animation: ${spin} 1s linear infinite;
`;

const TableFlexFill = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;

    & > div {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
    }
`;

function uniqueSortedStrings(values) {
    return [...new Set(values.map((v) => String(v ?? "").trim()).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, "pt-BR", { sensitivity: "base" })
    );
}

function toValueSet(selected) {
    if (!Array.isArray(selected) || selected.length === 0) return null;
    return new Set(
        selected.map((item) =>
            item != null && typeof item === "object" && "value" in item ? item.value : item
        )
    );
}

function aplicarFiltros(lista, filtros) {
    const obrasSet = toValueSet(filtros.selectedObras);
    const contratosSet = toValueSet(filtros.selectedContratos);
    const fontesSet = toValueSet(filtros.selectedFontes);
    const tiposSet = toValueSet(filtros.selectedTipoNc);
    const processosSet = toValueSet(filtros.selectedProcessos);
    const statusSet = toValueSet(filtros.selectedStatus);
    const situacaoSet = toValueSet(filtros.selectedSituacao);
    const preenchidosSet = toValueSet(filtros.selectedPreenchidos);

    return lista.filter((row) => {
        if (obrasSet && !obrasSet.has(String(row.centro_custo ?? ""))) return false;
        if (contratosSet && !contratosSet.has(String(row.contrato ?? ""))) return false;
        if (fontesSet && !fontesSet.has(String(row.fonte ?? ""))) return false;
        if (tiposSet && !tiposSet.has(String(row.tipo_nc ?? ""))) return false;
        if (processosSet && !processosSet.has(String(row.setor ?? ""))) return false;
        if (statusSet && !statusSet.has(String(row.status ?? ""))) return false;
        if (situacaoSet && !situacaoSet.has(String(row.STATUS ?? ""))) return false;
        if (preenchidosSet && !preenchidosSet.has(String(row.created_by ?? ""))) return false;
        return true;
    });
}

const RacBody = ({ racs, openEdit, onRacDeleted }) => {
    const api = useApi();
    const { user } = useContext(AuthContext);

    const podeApagar = useMemo(
        () => VerifyPermission(user, { ...PERMISSAO_BASE, funcao: "apagar" }),
        [user]
    );

    const [racParaDelete, setRacParaDelete] = useState(null);
    const [selectedObras, setSelectedObras] = useState([]);
    const [selectedContratos, setSelectedContratos] = useState([]);
    const [selectedFontes, setSelectedFontes] = useState([]);
    const [selectedTipoNc, setSelectedTipoNc] = useState([]);
    const [selectedProcessos, setSelectedProcessos] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState([]);
    const [selectedSituacao, setSelectedSituacao] = useState([]);
    const [selectedPreenchidos, setSelectedPreenchidos] = useState([]);
    const [clickedBtns, setClickedBtns] = useState({});

    const filtros = useMemo(
        () => ({
            selectedObras,
            selectedContratos,
            selectedFontes,
            selectedTipoNc,
            selectedProcessos,
            selectedStatus,
            selectedSituacao,
            selectedPreenchidos,
        }),
        [
            selectedObras,
            selectedContratos,
            selectedFontes,
            selectedTipoNc,
            selectedProcessos,
            selectedStatus,
            selectedSituacao,
            selectedPreenchidos,
        ]
    );

    const racsFiltrados = useMemo(
        () => aplicarFiltros(Array.isArray(racs) ? racs : [], filtros),
        [racs, filtros]
    );

    const estatisticas = useMemo(
        () => ({
            emAberto: racsFiltrados.filter((item) => item.status === "EM ABERTO").length,
            finalizado: racsFiltrados.filter((item) => item.status === "FINALIZADO").length,
            noPrazo: racsFiltrados.filter((item) => item.STATUS === "NO PRAZO").length,
            atrasado: racsFiltrados.filter((item) => item.STATUS === "ATRASADO").length,
        }),
        [racsFiltrados]
    );

    const opcoesObrasMs = useMemo(
        () => uniqueSortedStrings(racs.map((r) => r.centro_custo)).map((opt) => ({ value: opt, label: opt })),
        [racs]
    );
    const opcoesContratosMs = useMemo(
        () => uniqueSortedStrings(racs.map((r) => r.contrato)).map((opt) => ({ value: opt, label: opt })),
        [racs]
    );
    const opcoesFontesMs = useMemo(
        () => uniqueSortedStrings(racs.map((r) => r.fonte)).map((opt) => ({ value: opt, label: opt })),
        [racs]
    );
    const opcoesTipoNcMs = useMemo(
        () => uniqueSortedStrings(racs.map((r) => r.tipo_nc)).map((opt) => ({ value: opt, label: opt })),
        [racs]
    );
    const opcoesProcessosMs = useMemo(
        () => uniqueSortedStrings(racs.map((r) => r.setor)).map((opt) => ({ value: opt, label: opt })),
        [racs]
    );
    const opcoesPreenchidosMs = useMemo(
        () => uniqueSortedStrings(racs.map((r) => r.created_by)).map((opt) => ({ value: opt, label: opt })),
        [racs]
    );
    const opcoesStatusMs = useMemo(
        () => [
            { value: "FINALIZADO", label: "FINALIZADO" },
            { value: "EM ABERTO", label: "EM ABERTO" },
        ],
        []
    );
    const opcoesSituacaoMs = useMemo(
        () => [
            { value: "ATRASADO", label: "ATRASADO" },
            { value: "NO PRAZO", label: "NO PRAZO" },
        ],
        []
    );

    const downloadRelat = useCallback(async (e, relatID) => {
        e.stopPropagation();

        setClickedBtns((prev) => ({
            ...prev,
            [relatID]: true,
        }));

        try {
            const { data } = await api({
                url:
                    "/Painel/API/RAC/DownloadRelat/index.php?id=" +
                    relatID,
                method: "GET",
                responseType: "blob",
            });

            const blob = new Blob([data], { type: "aplication/pdf" });
            saveAs(blob, "arquivo.pdf");
        } catch (error) {
            console.log(error);
        } finally {
            setClickedBtns((prev) => ({
                ...prev,
                [relatID]: false,
            }));
        }
    }, [api]);

    const tableRef = useRef(null);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
        if (tableRef.current) {
            tableRef.current.scrollTop = 0;
        }
    }, [racsFiltrados]);

    const loadMoreRows = useCallback(() => {
        setVisibleCount((prev) => {
            const total = racsFiltrados.length;
            if (prev >= total) return prev;
            return Math.min(prev + PAGE_SIZE, total);
        });
    }, [racsFiltrados.length]);

    const abrirDelete = useCallback((e, row) => {
        e.stopPropagation();
        setRacParaDelete(row);
    }, []);

    const fecharDelete = useCallback(() => {
        setRacParaDelete(null);
    }, []);

    const columns = useMemo(
        () => [
            {
                id: "id",
                header: "ID",
                accessor: "id",
                sortable: true,
                width: "70px",
                align: "center",
            },
            {
                id: "id_relacional",
                header: "ID RELACIONAL",
                accessor: (row) =>
                    row.fonte === "AUDITORIA INTERNA"
                        ? `${row.id_auditoria} - ${row.id_item}`
                        : "-",
                sortable: true,
                render: (_, row) =>
                    row.fonte === "AUDITORIA INTERNA"
                        ? `${row.id_auditoria} - ${row.id_item}`
                        : "-",
            },
            {
                id: "fonte",
                header: "FONTE",
                accessor: "fonte",
                sortable: true,
            },
            {
                id: "centro_custo",
                header: "CENTRO CUSTO",
                accessor: "centro_custo",
                sortable: true,
            },
            {
                id: "contrato",
                header: "CONTRATO",
                accessor: "contrato",
                sortable: true,
            },
            {
                id: "tipo_nc",
                header: "TIPO",
                accessor: "tipo_nc",
                sortable: true,
            },
            {
                id: "setor",
                header: "PROCESSO",
                accessor: "setor",
                sortable: true,
            },
            {
                id: "status",
                header: "STATUS",
                accessor: "status",
                sortable: true,
            },
            {
                id: "when",
                header: "PRAZO",
                accessor: "when",
                sortable: true,
                width: "120px",
                render: (value) => (value ? formatarData(value) : "-"),
                sortValue: (row) => row.when ?? "",
            },
            {
                id: "encerramento",
                header: "ENCERRAMENTO",
                accessor: "encerramento",
                sortable: true,
                width: "120px",
                render: (value) => (value ? formatarData(value) : "-"),
                sortValue: (row) => row.encerramento ?? "",
            },
            {
                id: "situacao",
                header: "SITUAÇÃO",
                accessor: "STATUS",
                sortable: true,
                width: "110px",
            },
            {
                id: "acoes",
                header: "",
                sortable: false,
                width: podeApagar ? "90px" : "50px",
                align: "center",
                render: (_, row) => (
                    <AcoesCelula>
                        {clickedBtns[row.id] ? (
                            <BotaoAcao type="button" disabled title="Baixando...">
                                <IconLoad size={14} />
                            </BotaoAcao>
                        ) : (
                            <BotaoAcao
                                type="button"
                                title="Baixar anexo"
                                onClick={(e) => downloadRelat(e, row.id)}
                            >
                                <FiDownload size={14} />
                            </BotaoAcao>
                        )}
                        {podeApagar && (
                            <BotaoAcao
                                type="button"
                                $danger
                                title="Remover"
                                onClick={(e) => abrirDelete(e, row)}
                            >
                                <FiTrash2 size={14} />
                            </BotaoAcao>
                        )}
                    </AcoesCelula>
                ),
            },
        ],
        [clickedBtns, downloadRelat, podeApagar, abrirDelete]
    );

    return (
        <>
            <PageContent>
                <GenericHeader responsive>
                    <Filters columns={4}>
                        <MultiselectCheck
                            options={opcoesObrasMs}
                            selectedOptions={selectedObras}
                            setSelectedOptions={setSelectedObras}
                            placeholder="CENTRO CUSTO..."
                        />
                        <MultiselectCheck
                            options={opcoesContratosMs}
                            selectedOptions={selectedContratos}
                            setSelectedOptions={setSelectedContratos}
                            placeholder="CONTRATO..."
                        />
                        <MultiselectCheck
                            options={opcoesFontesMs}
                            selectedOptions={selectedFontes}
                            setSelectedOptions={setSelectedFontes}
                            placeholder="FONTE..."
                        />
                        <MultiselectCheck
                            options={opcoesTipoNcMs}
                            selectedOptions={selectedTipoNc}
                            setSelectedOptions={setSelectedTipoNc}
                            placeholder="TIPO NC..."
                        />
                        <MultiselectCheck
                            options={opcoesProcessosMs}
                            selectedOptions={selectedProcessos}
                            setSelectedOptions={setSelectedProcessos}
                            placeholder="PROCESSO..."
                        />
                        <MultiselectCheck
                            options={opcoesStatusMs}
                            selectedOptions={selectedStatus}
                            setSelectedOptions={setSelectedStatus}
                            placeholder="STATUS..."
                        />
                        <MultiselectCheck
                            options={opcoesSituacaoMs}
                            selectedOptions={selectedSituacao}
                            setSelectedOptions={setSelectedSituacao}
                            placeholder="SITUAÇÃO..."
                        />
                        <MultiselectCheck
                            options={opcoesPreenchidosMs}
                            selectedOptions={selectedPreenchidos}
                            setSelectedOptions={setSelectedPreenchidos}
                            placeholder="PREENCHIDO POR..."
                        />
                    </Filters>

                    <Statistics>
                        <ContainerStatistic>
                            <TituloStatistic>POR STATUS</TituloStatistic>
                            <TextoStatistic>EM ABERTO: {estatisticas.emAberto}</TextoStatistic>
                            <TextoStatistic>FINALIZADO: {estatisticas.finalizado}</TextoStatistic>
                        </ContainerStatistic>
                    </Statistics>

                    <Statistics>
                        <ContainerStatistic>
                            <TituloStatistic>POR SITUAÇÃO</TituloStatistic>
                            <TextoStatistic>NO PRAZO: {estatisticas.noPrazo}</TextoStatistic>
                            <TextoStatistic>ATRASADO: {estatisticas.atrasado}</TextoStatistic>
                        </ContainerStatistic>
                    </Statistics>
                </GenericHeader>

                <TableFlexFill>
                    <SortableTable
                        ref={tableRef}
                        columns={columns}
                        data={racsFiltrados}
                        limit={visibleCount}
                        onScrollReachEnd={loadMoreRows}
                        scrollReachThreshold={SCROLL_THRESHOLD}
                        emptyText="Nenhuma RAC encontrada."
                        initialSort={{ key: "id", direction: "desc" }}
                        getRowId={(row) => row.id}
                        onRowClick={(row) => openEdit(row.id)}
                        rowCursor="pointer"
                    />
                </TableFlexFill>
            </PageContent>

            {racParaDelete && (
                <ModalDeleteRac
                    rac={racParaDelete}
                    fecharModal={fecharDelete}
                    onDeleted={onRacDeleted}
                />
            )}
        </>
    );
};

export default RacBody;
